import React, { useState, useEffect } from 'react'
import { api, formatCurrency, formatDate } from '../../utils/api'
import './affiliates.css'

// Admin-only: referral partners. Each has one commission rate applied to every
// customer they refer. Expand a referrer to see referred customers, the
// commission ledger, what's owed, and to record a payout.
function Referrals() {
  const [referrers, setReferrers] = useState([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [expandedId, setExpandedId] = useState(null)

  useEffect(() => { loadReferrers() }, [])

  async function loadReferrers() {
    setLoading(true)
    try {
      const res = await api.get('/api/affiliates/referrers')
      setReferrers(res.referrers || [])
    } catch (error) {
      console.error('Failed to load referrers:', error)
    } finally {
      setLoading(false)
    }
  }

  async function createReferrer(body) {
    await api.post('/api/affiliates/referrers', body)
    setAdding(false)
    await loadReferrers()
  }

  async function patchReferrer(id, body) {
    await api.patch(`/api/affiliates/referrers/${id}`, body)
    setEditingId(null)
    await loadReferrers()
  }

  async function deleteReferrer(id, name) {
    if (!confirm(`Deactivate ${name}? Their portal stops working and they stop accruing new commission. History is kept.`)) return
    try {
      await api.delete(`/api/affiliates/referrers/${id}`)
      await loadReferrers()
    } catch (error) {
      console.error('Failed to deactivate referrer:', error)
    }
  }

  if (loading) return <div className="affiliate-loading">Loading referrers…</div>

  return (
    <div className="affiliate-page">
      <div className="affiliate-head">
        <p className="text-sm text-muted">
          Add a partner who refers customers to us. They get a no-login portal to add the customers they're referring; we owe them their % of each referred trade.
        </p>
        {!adding && <button className="btn btn-sm btn-primary" onClick={() => setAdding(true)}>Add referrer</button>}
      </div>

      {adding && <ReferrerForm onSave={createReferrer} onCancel={() => setAdding(false)} />}

      {referrers.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">No referrers yet</div>
          <div className="empty-state-description">Add a partner (e.g. Soundgas) to generate their referral portal.</div>
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr><th></th><th>Referrer</th><th>Portal</th><th>Rate</th><th>Owed</th><th></th></tr>
          </thead>
          <tbody>
            {referrers.map(r => (
              <ReferrerRow
                key={r.referrer_id}
                referrer={r}
                expanded={expandedId === r.referrer_id}
                editing={editingId === r.referrer_id}
                onToggle={() => setExpandedId(expandedId === r.referrer_id ? null : r.referrer_id)}
                onEdit={() => setEditingId(r.referrer_id)}
                onCancelEdit={() => setEditingId(null)}
                onSave={(body) => patchReferrer(r.referrer_id, body)}
                onDelete={() => deleteReferrer(r.referrer_id, r.name)}
                onChanged={loadReferrers}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function ReferrerRow({ referrer, expanded, editing, onToggle, onEdit, onCancelEdit, onSave, onDelete, onChanged }) {
  const portalPath = `/marketing/referrals/${referrer.slug}`
  const portalUrl = `${window.location.origin}${portalPath}`

  if (editing) {
    return <tr><td colSpan={6}><ReferrerForm initial={referrer} onSave={onSave} onCancel={onCancelEdit} /></td></tr>
  }

  return (
    <>
      <tr style={referrer.active ? undefined : { opacity: 0.5 }}>
        <td>
          <button className="btn btn-secondary" onClick={onToggle} title="Show ledger" style={{ minWidth: 32 }}>{expanded ? '▾' : '▸'}</button>
        </td>
        <td>
          <div>{referrer.name}</div>
          {referrer.contact_email && <span className="text-sm text-muted">{referrer.contact_email}</span>}
        </td>
        <td><a href={portalPath} target="_blank" rel="noreferrer"><code>/{referrer.slug}</code></a></td>
        <td><span className="badge badge-success">{referrer.bonus_pct}%</span></td>
        <td>{Number(referrer.total_owed) > 0 ? <strong>{formatCurrency(referrer.total_owed)}</strong> : <span className="text-sm text-muted">-</span>}</td>
        <td style={{ whiteSpace: 'nowrap' }}>
          <button className="btn btn-secondary" onClick={onEdit}>Edit</button>{' '}
          <button className="btn btn-secondary" onClick={onDelete}>Delete</button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6}>
            <ReferrerDetail referrer={referrer} portalUrl={portalUrl} onChanged={onChanged} />
          </td>
        </tr>
      )}
    </>
  )
}

function ReferrerDetail({ referrer, portalUrl, onChanged }) {
  const [customers, setCustomers] = useState([])
  const [commissions, setCommissions] = useState([])
  const [payments, setPayments] = useState([])
  const [totalOwed, setTotalOwed] = useState(0)
  const [loading, setLoading] = useState(true)
  const [addingCustomer, setAddingCustomer] = useState(false)
  const [paying, setPaying] = useState(false)

  useEffect(() => { loadDetail() }, [referrer.referrer_id])

  async function loadDetail() {
    setLoading(true)
    try {
      const [c, comm, pay] = await Promise.all([
        api.get(`/api/affiliates/referrers/${referrer.referrer_id}/referred-customers`),
        api.get(`/api/affiliates/referrers/${referrer.referrer_id}/commissions`),
        api.get(`/api/affiliates/referrers/${referrer.referrer_id}/payments`),
      ])
      setCustomers(c.referred_customers || [])
      setCommissions(comm.commissions || [])
      setTotalOwed(comm.total_owed || 0)
      setPayments(pay.payments || [])
    } catch (error) {
      console.error('Failed to load referrer detail:', error)
    } finally {
      setLoading(false)
    }
  }

  async function addCustomer(body) {
    await api.post(`/api/affiliates/referrers/${referrer.referrer_id}/referred-customers`, body)
    setAddingCustomer(false)
    await loadDetail()
  }

  async function removeCustomer(id, name) {
    if (!confirm(`Remove referred customer ${name}? They'll stop accruing new commission. History is kept.`)) return
    try {
      await api.delete(`/api/affiliates/referred-customers/${id}`)
      await loadDetail()
    } catch (error) {
      console.error('Failed to remove referred customer:', error)
    }
  }

  async function pay() {
    if (!confirm(`Record a payment of ${formatCurrency(totalOwed)} to ${referrer.name}? This settles all outstanding commissions.`)) return
    setPaying(true)
    try {
      await api.post(`/api/affiliates/referrers/${referrer.referrer_id}/pay`, {})
      await loadDetail()
      onChanged?.()
    } catch (error) {
      console.error('Payment failed:', error)
      alert(error?.message || 'Payment failed')
    } finally {
      setPaying(false)
    }
  }

  if (loading) return <div className="card" style={{ padding: 16 }}>Loading ledger...</div>

  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="text-sm text-muted" style={{ marginBottom: 12 }}>
        Portal: <a href={portalUrl} target="_blank" rel="noreferrer">{portalUrl}</a>
      </div>

      <SectionHeading title="Referred customers">
        {!addingCustomer && <button className="btn btn-secondary" onClick={() => setAddingCustomer(true)}>Add customer</button>}
      </SectionHeading>
      {addingCustomer && <ReferredCustomerForm onSave={addCustomer} onCancel={() => setAddingCustomer(false)} />}
      {customers.length === 0 ? (
        <p className="text-sm text-muted">None yet. The partner can add these on their portal, or add them here.</p>
      ) : (
        <table className="table">
          <thead><tr><th>Name</th><th>Domain</th><th>Reward</th><th>Notes</th><th></th></tr></thead>
          <tbody>
            {customers.map(c => (
              <tr key={c.referred_customer_id}>
                <td>{c.name}</td>
                <td><code>@{c.email_domain}</code></td>
                <td>{c.reward_mode === 'both' ? <span className="badge badge-success">Customer boost + commission</span> : <span className="badge">Commission only</span>}</td>
                <td className="text-sm text-muted">{c.notes || '-'}</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="btn btn-sm btn-secondary" onClick={() => removeCustomer(c.referred_customer_id, c.name)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <SectionHeading title="Commission ledger">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>Owed: <strong>{formatCurrency(totalOwed)}</strong></span>
          <button className="btn btn-primary" onClick={pay} disabled={paying || totalOwed <= 0}>
            {paying ? 'Recording...' : `Pay ${referrer.name}`}
          </button>
        </div>
      </SectionHeading>
      {commissions.length === 0 ? (
        <p className="text-sm text-muted">No commissions yet. They accrue when a referred customer's trade reaches Closed Traded.</p>
      ) : (
        <table className="table">
          <thead><tr><th>Date</th><th>Trade</th><th>Trade value</th><th>Rate</th><th>Commission</th><th>Status</th></tr></thead>
          <tbody>
            {commissions.map(c => (
              <tr key={c.referral_commission_id}>
                <td>{formatDate(c.accrued_at)}</td>
                <td>{c.deal_name || c.customer_email || '-'}</td>
                <td>{formatCurrency(c.trade_amount)}</td>
                <td>{c.bonus_pct}%</td>
                <td><strong>{formatCurrency(c.commission_amount)}</strong></td>
                <td>{c.status === 'paid' ? <span className="badge badge-success">Paid</span> : <span className="badge">Due</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {payments.length > 0 && (
        <>
          <SectionHeading title="Payment history" />
          <table className="table">
            <thead><tr><th>Date</th><th>Amount</th><th>Reference</th></tr></thead>
            <tbody>
              {payments.map(p => (
                <tr key={p.referral_payment_id}>
                  <td>{formatDate(p.paid_at)}</td>
                  <td><strong>{formatCurrency(p.amount)}</strong></td>
                  <td className="text-sm text-muted">{p.reference || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  )
}

function SectionHeading({ title, children }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '20px 0 8px' }}>
      <h3 style={{ margin: 0, fontSize: 15 }}>{title}</h3>
      {children}
    </div>
  )
}

function ReferrerForm({ initial, onSave, onCancel }) {
  const [name, setName] = useState(initial?.name || '')
  const [email, setEmail] = useState(initial?.contact_email || '')
  const [slug, setSlug] = useState(initial?.slug || '')
  const [pct, setPct] = useState(initial?.bonus_pct ?? 5)
  const [notes, setNotes] = useState(initial?.notes || '')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  async function submit() {
    setError('')
    if (!name.trim()) return setError('Name is required')
    if (!slug.trim()) return setError('Customer URL is required')
    setSaving(true)
    try {
      await onSave({ name: name.trim(), email: email.trim() || null, slug: slug.trim().toLowerCase(), bonus_pct: Number(pct), notes: notes.trim() || null })
    } catch (e) {
      setError(e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="affiliate-form">
        <div className="filter-group">
          <label className="filter-label">Name</label>
          <input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="Soundgas" maxLength={200} />
        </div>
        <div className="filter-group">
          <label className="filter-label">Email</label>
          <input className="form-input" value={email} onChange={e => setEmail(e.target.value)} placeholder="tony@soundgas.com" maxLength={200} />
        </div>
        <div className="filter-group">
          <label className="filter-label">Customer URL (slug)</label>
          <input className="form-input" value={slug} onChange={e => setSlug(e.target.value)} placeholder="soundgas" maxLength={80} />
        </div>
        <div className="filter-group narrow">
          <label className="filter-label">Bonus %</label>
          <input className="form-input" type="number" min="0" max="100" step="0.5" value={pct} onChange={e => setPct(e.target.value)} />
        </div>
      </div>
      <div className="filter-group filter-group-wide" style={{ marginTop: 12 }}>
        <label className="filter-label">Notes</label>
        <textarea className="form-input" value={notes} onChange={e => setNotes(e.target.value)} rows={2} maxLength={2000} />
      </div>
      {error && <div className="text-sm" style={{ color: 'var(--danger, #c0392b)', marginTop: 8 }}>{error}</div>}
      <div className="filter-actions" style={{ marginTop: 12 }}>
        <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>{' '}
        <button className="btn btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </div>
  )
}

function ReferredCustomerForm({ onSave, onCancel }) {
  const [name, setName] = useState('')
  const [domain, setDomain] = useState('')
  const [rewardMode, setRewardMode] = useState('referrer_only')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  async function submit() {
    setError('')
    if (!name.trim()) return setError('Name is required')
    if (!domain.trim()) return setError('Domain is required')
    setSaving(true)
    try {
      await onSave({ name: name.trim(), email_domain: domain.trim(), reward_mode: rewardMode, notes: notes.trim() || null })
    } catch (e) {
      setError(e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card" style={{ padding: 16, marginBottom: 12 }}>
      <div className="affiliate-form">
        <div className="filter-group">
          <label className="filter-label">Name</label>
          <input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="Geoff at Reverb" maxLength={200} />
        </div>
        <div className="filter-group">
          <label className="filter-label">Domain</label>
          <input className="form-input" value={domain} onChange={e => setDomain(e.target.value)} placeholder="@reverb.com" maxLength={200} />
        </div>
        <div className="filter-group">
          <label className="filter-label">Reward</label>
          <select className="form-input" value={rewardMode} onChange={e => setRewardMode(e.target.value)}>
            <option value="referrer_only">Commission only</option>
            <option value="both">Customer boost + commission</option>
          </select>
        </div>
      </div>
      <div className="filter-group filter-group-wide" style={{ marginTop: 12 }}>
        <label className="filter-label">Notes</label>
        <textarea className="form-input" value={notes} onChange={e => setNotes(e.target.value)} rows={2} maxLength={2000} />
      </div>
      {error && <div className="text-sm" style={{ color: 'var(--danger, #c0392b)', marginTop: 8 }}>{error}</div>}
      <div className="filter-actions" style={{ marginTop: 12 }}>
        <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving ? 'Saving...' : 'Add'}</button>{' '}
        <button className="btn btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </div>
  )
}

export default Referrals
